/**
 * 등록 전송 직전 원산지·대상 말 고치기 — 1688(중국 공급처) 상품의 상품명·검색태그에서
 * «국산·국내산·한국산·메이드인코리아» 원산지 주장과 근거 없는 «여아·유아…» 대상 말을 빼고 보낸다(#240-2 · PR261017-2A7068·3BB158).
 * 판정은 seoOriginGuard(자동 상품명 가드)를 그대로 쓴다 — 같은 규칙을 두 번 쓰지 않는다.
 * 두 전송 길이 같이 쓴다: 화면(AIRocketReg submitProductsToCoupang) · 도구(tool-exec/rocket-submit.js, tool-exec/origin-claim.js 가 올린다).
 * 고친 뒤에도 상품명이 비거나 원산지 말만 남으면(뺄 수 없음) invalid 사유를 준다 — 그 건만 보내지 않는다.
 * 의존성은 ./seoOriginGuard 하나뿐인 순수 파일 — 단위시험 __tests__/submitOriginFix.test.mjs
 */
import { isChinaSourced, dropOriginClaims, dropOriginClaimTags, dropKidsWordsNotInSource } from './seoOriginGuard';

// 규칙(낱말 목록·근거)은 여기 두지 않는다 — 전부 seoOriginGuard 함수로만 판정한다(이미 보낸 상품 고치기와 같은 결과가 나오게)

function srcOf(product) {
  const p = product || {};
  return {
    attributes: p.attributes, options: p.options, productFeature: p.productFeature || '', targetKeyword: p.targetKeyword || '',
    originalTitle: p.productName, purchaseLink: p['구매링크']
  };
}

/** 상품명 하나 — 대상 말 → 원산지 말 순서로 뺀다 · 다 빠지면 빈 글(원래 이름 되살림 0 — 부른 쪽이 그 칸 안 고치고 보내지 않음) */
function fixName(name, src, words) {
  if (!name || typeof name !== 'string') return name;
  const k = dropKidsWordsNotInSource(name, src, { allowEmpty: true });
  k.removed.forEach(w => words.kids.add(String(w).trim()));
  if (!k.name) return '';
  const o = dropOriginClaims(k.name, src, { allowEmpty: true });
  o.removed.forEach(w => words.origin.add(String(w).trim()));
  return o.name;
}

/** 태그 목록 — 원산지(낱말만 빼고 겹치면 하나) → 태그마다 대상 말 */
function fixTags(tags, src, words) {
  const o = dropOriginClaimTags(tags, src);
  o.removed.forEach(t => dropOriginClaims(String(t), src).removed.forEach(w => words.origin.add(String(w).trim())));
  const out = [];
  const seen = new Set();
  (o.keywords || []).forEach(t => {
    const k = dropKidsWordsNotInSource(String(t), src, { allowEmpty: true });   // 대상 말뿐인 태그는 빈 글 → 아래에서 뺌(되살림 0)
    k.removed.forEach(w => words.kids.add(String(w).trim()));
    const key = String(k.name).replace(/\s+/g, '');
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(k.name);
  });
  return out;
}

/**
 * @param {object} product - RocketReg 문서(productName=1688 원 제목, 구매링크, attributes, options, productFeature, targetKeyword, seo, coupangDoc)
 * @returns {{ patch: Object<string, any>, changes: string[], invalid: string }}
 *   patch = 바뀐 칸만(점 경로 → 새 값, 저장·화면 반영용) · changes = 셀러에게 보일 문장 · invalid = 보내면 안 되는 사유('' 이면 보내도 됨)
 */
export function fixOriginForSubmit(product) {
  const none = { patch: {}, changes: [], invalid: '' };
  if (!product) return none;
  const src = srcOf(product);
  if (!isChinaSourced(src)) return none;
  const words = { origin: new Set(), kids: new Set() };
  const patch = {};
  const names = [];
  const setName = (path, value) => {
    if (!value || typeof value !== 'string') return;
    const v = fixName(value, src, words);
    names.push(v);
    if (v && v !== value) patch[path] = v;
  };
  const seo = product.seo || {};
  setName('seo.productName', seo.productName);
  if (Array.isArray(seo.keywords)) {
    const t = fixTags(seo.keywords, src, words);
    if (JSON.stringify(t) !== JSON.stringify(seo.keywords)) patch['seo.keywords'] = t;
  }
  // 화면 목록이 쓰는 사본(productNameSeo·keywordsSeo)도 같이 — 고친 이름과 화면이 어긋나지 않게
  setName('productNameSeo', product.productNameSeo);
  if (Array.isArray(product.keywordsSeo)) {
    const t = fixTags(product.keywordsSeo, src, words);
    if (JSON.stringify(t) !== JSON.stringify(product.keywordsSeo)) patch.keywordsSeo = t;
  }
  const doc = product.coupangDoc || {};
  setName('coupangDoc.productName', doc.productName);
  (Array.isArray(doc.jsonDocument) ? doc.jsonDocument : []).forEach((d, i) => {
    if (!d) return;
    if (d.startPage) setName(`coupangDoc.jsonDocument.${i}.startPage.productName`, d.startPage.productName);
    const pp = d.productPage;
    if (!pp) return;
    if (pp.modelNumber && typeof pp.modelNumber === 'string') {
      const v = fixName(pp.modelNumber, src, words);
      names.push(v);
      if (v && v !== pp.modelNumber) patch[`coupangDoc.jsonDocument.${i}.productPage.modelNumber`] = v;
    }
    if (typeof pp.searchTags === 'string' && pp.searchTags.trim()) {
      const list = pp.searchTags.split(/\s*,\s*/).filter(Boolean);
      const t = fixTags(list, src, words).join(', ');
      if (t !== list.join(', ')) patch[`coupangDoc.jsonDocument.${i}.productPage.searchTags`] = t;
    }
  });
  const changes = [];
  if (words.origin.size) changes.push(`원산지 말 고쳐서 보냄: ${[...words.origin].join('·')} → 삭제 (1688 상품은 중국산)`);
  if (words.kids.size) changes.push(`근거 없는 대상 말 고쳐서 보냄: ${[...words.kids].join('·')} → 삭제`);
  // 고친 뒤 이름(상품명·모델명)이 비면 보내지 않는다 — 가드는 allowEmpty 로 빈 글을 돌려주고, 그 칸은 고치지 않는다(거짓 이름 되살림 0 · 빈 이름 저장 0)
  // 원산지 말이 아직 남은 이름도 막는다(태그 가드로 다시 잼)
  const left = names.some(n => dropOriginClaimTags([String(n || '')], src).removed.length > 0);
  const empty = names.some(n => !String(n || '').trim());
  const invalid = !(left || empty) ? ''
    : words.origin.size || left
      ? '원산지 표기(국산) — 1688 상품은 중국산인데 상품명에서 원산지 말을 빼면 이름이 남지 않아 고치지 못했습니다. 상품명을 새로 정해 SEO 저장 뒤 다시 전송하세요.'
      : `근거 없는 대상 말(${[...words.kids].join('·')}) — 상품명에서 빼면 이름이 남지 않아 고치지 못했습니다. 상품명을 새로 정해 SEO 저장 뒤 다시 전송하세요.`;
  return { patch, changes, invalid };
}

export default { fixOriginForSubmit };
